import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Motion";
import { RECENT_WORK } from "@/data/site";

export default function Gallery() {
  const [active, setActive] = useState(null); // index or null

  const close = () => setActive(null);
  const prev = () => setActive((i) => (i === 0 ? RECENT_WORK.length - 1 : i - 1));
  const next = () => setActive((i) => (i === RECENT_WORK.length - 1 ? 0 : i + 1));

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [active]);

  return (
    <div>
      <section className="pt-40 pb-16 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <p className="kicker mb-6">Our recent work</p>
          <h1 className="font-display font-extrabold tracking-tight text-[#1C2A39] text-5xl sm:text-7xl leading-[0.95]">
            <MaskedLines lines={["Freshly cleaned", "spaces."]} />
          </h1>
          <Reveal delay={0.5}><p className="mt-7 text-lg text-[#475569] max-w-xl">A look at real homes we've recently transformed across Adelaide — bathrooms, kitchens, carpets and full end-of-lease cleans.</p></Reveal>
        </div>
      </section>

      <section className="pb-24 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
            {RECENT_WORK.map((w, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06} className="mb-6 break-inside-avoid">
                <button
                  data-testid={`work-item-${i}`}
                  onClick={() => setActive(i)}
                  className="group relative block w-full overflow-hidden rounded-3xl border border-slate-200 bg-white text-left"
                >
                  <div className="img-hover-wrap">
                    <img src={w.url} alt={w.label} loading="lazy" className="w-full object-cover" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-500">
                    <span className="kicker !text-[#5EEAD4]">{w.tag}</span>
                    <p className="font-display text-white text-xl mt-1">{w.label}</p>
                  </div>
                  <span className="absolute top-4 left-4 text-[11px] px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[#1C2A39] font-medium">{w.tag}</span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#E8F1EB]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display font-medium text-4xl sm:text-5xl tracking-tight text-[#1C2A39]">Want your space to look like this?</h2>
          <Link to="/quote" className="btn-primary mt-8">Get a Free Quote <ArrowRight size={18} /></Link>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
            onClick={close}
          >
            <button data-testid="lightbox-close" onClick={close} className="absolute top-5 right-5 text-white/80 hover:text-white transition-colors" aria-label="Close"><X size={30} /></button>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 sm:left-8 text-white/80 hover:text-white transition-colors" aria-label="Previous"><ChevronLeft size={40} /></button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 sm:right-8 text-white/80 hover:text-white transition-colors" aria-label="Next"><ChevronRight size={40} /></button>
            <motion.div
              key={active}
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}
            >
              <img src={RECENT_WORK[active].url} alt={RECENT_WORK[active].label} className="w-full max-h-[80vh] object-contain rounded-2xl" />
              <div className="text-center mt-4">
                <span className="kicker !text-[#5EEAD4]">{RECENT_WORK[active].tag}</span>
                <p className="font-display text-white text-2xl mt-1">{RECENT_WORK[active].label}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
